import { choqueDe, diaNegocio, enlaceWhatsApp, formatearMoneda, formatearTelefono, hora12, horaLocal, instanteLocal, nombreServicios, serviciosDe, type CitaVista, type DetallesChoque } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheckBig, CircleSlash, FastForward, Hourglass, MessageCircle, Pencil, Play, Replace, UserX } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { Button } from '@/components/ui/button';
import { Campo, Textarea } from '@/components/ui/form';
import { Dialogo, PanelLateral } from '@/components/ui/overlay';
import { diaLargo } from '@/lib/utils';
import { useAgendaDia } from './datos';
import { NuevaCitaDialog } from './NuevaCitaDialog';
import { EstadoCita } from './EstadoCita';
import { PanelChoque } from './PanelChoque';
import { VerAgendaDialog } from './VerAgendaDialog';
import { SelectorHorario } from './SelectorHorario';
import { avisarError } from '@/lib/avisos';
import { Esqueleto } from '@/components/ui/data';
import { DetalleVenta, useVentaDetalle } from '../ventas/DetalleVenta';
import { CambiarServicioDialog } from './CambiarServicioDialog';

type Modo = 'ver' | 'editar' | 'adelantar' | 'cancelar';

export function CitaDetalle({ cita: citaInicial, onCerrar }: { cita: CitaVista; onCerrar: () => void }) {
  // La versión vigente de la cita: se refresca en tiempo real (p. ej. el barbero inicia el servicio en la app).
  const agendaDelDia = useAgendaDia(diaNegocio(new Date(citaInicial.inicio)));
  const cita = agendaDelDia.data?.citas.find((x) => x.id === citaInicial.id) ?? citaInicial;
  const { puede, usuario } = useSesion();
  const qc = useQueryClient();
  const [modo, setModo] = useState<Modo>('ver');
  const [dia, setDia] = useState(diaNegocio(new Date(cita.inicio)));
  const [hora, setHora] = useState('');
  /** Al adelantar, la hora chocó con otra cita del barbero. */
  const [choque, setChoque] = useState<DetallesChoque | undefined>();
  const [verAgenda, setVerAgenda] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [confirmo, setConfirmo] = useState(false);
  const [confirmarFin, setConfirmarFin] = useState(false);
  const [cambiando, setCambiando] = useState<{ id: string; nombre: string } | null>(null);
  const ventaId = cita.atencion?.ventaId ?? null;
  const verCarrito = puede('ventas.ver_detalle');
  const carrito = useVentaDetalle(verCarrito ? ventaId : null);
  const precioServicios = serviciosDe(cita).reduce((suma, s) => suma + s.precio, 0);
  // Ya cobrada, lo que cuenta es lo que se cobró (puede diferir del precio de lista tras una corrección).
  const cobrada = cita.estado === 'finalizada' && carrito.data?.estado === 'pagada';

  const alTerminar = (mensaje: string) => {
    toast.success(mensaje);
    void qc.invalidateQueries({ queryKey: ['agenda'] });
    void qc.invalidateQueries({ queryKey: ['pendientes'] });
    onCerrar();
  };
  const accion = useMutation({
    /** `horaElegida`: la opción Antes/Después del choque, que se guarda de inmediato. */
    mutationFn: async (horaElegida: string | void) => {
      if (modo === 'adelantar') {
        const inicio = instanteLocal(dia, horaElegida || hora).toISOString();
        return api(`/citas/${cita.id}/adelantar`, { method: 'POST', body: { inicio, confirmoCliente: true } });
      }
      return api(`/citas/${cita.id}/cancelar`, { method: 'POST', body: { motivo } });
    },
    onSuccess: () =>
      alTerminar(
        { adelantar: 'Cita adelantada', cancelar: 'Cita cancelada', ver: '', editar: '' }[modo],
      ),
    onError: (e) => {
      const c = modo === 'adelantar' ? choqueDe(e) : null;
      setChoque(c ?? undefined);
      if (!c) avisarError(e);
    },
  });

  /** Atención del servicio desde la agenda (lo mismo que hace el barbero en la app). */
  const atencion = useMutation({
    mutationFn: (paso: 'iniciar' | 'no-presentado' | 'finalizar') =>
      paso === 'finalizar'
        ? api(`/atencion/por-cita/${cita.id}/finalizar`, { method: 'POST', body: {} })
        : api(`/atencion/${cita.id}/${paso}`, { method: 'POST', body: {} }),
    onSuccess: (_, paso) => {
      if (paso === 'finalizar') void qc.invalidateQueries({ queryKey: ['ventas'] });
      alTerminar(
        {
          iniciar: 'Servicio iniciado: el carrito ya está en caja',
          'no-presentado': 'Tolerancia iniciada: si no llega, la cita se cancela sola',
          finalizar: 'Servicio finalizado: listo para cobrar en caja',
        }[paso],
      );
    },
    onError: (e) => {
      setConfirmarFin(false);
      avisarError(e);
    },
  });
  const esHoy = diaNegocio(new Date(cita.inicio)) === diaNegocio(new Date());
  const atiende = puede('agenda.reprogramar') && esHoy;
  const puedeIniciar = atiende && (cita.estado === 'agendada' || cita.estado === 'en_tolerancia');
  const restanteTolerancia = cita.toleranciaHasta ? Math.max(0, Math.ceil((new Date(cita.toleranciaHasta).getTime() - Date.now()) / 60_000)) : null;

  // Corrige el servicio mientras se atiende: el barbero de la cita, o quien puede reprogramar (recepción, dueño).
  const esSuBarbero = !!usuario && cita.barberoId === usuario.id && (puede('atencion.operar') || puede('agenda.recibir_citas'));
  const puedeCambiarServicio = cita.estado === 'en_servicio' && !!ventaId && (esSuBarbero || puede('agenda.reprogramar'));
  const idsServicios = serviciosDe(cita).map((s) => s.id);
  const botonCambiar = (servicio: { id: string; nombre: string }) => (
    <Button variante="enlace" tamano="sm" className="h-auto py-1 text-[12.5px]" aria-label={`Cambiar servicio ${servicio.nombre}`} onClick={() => setCambiando(servicio)}>
      <Replace /> Cambiar servicio
    </Button>
  );

  const editable = cita.estado === 'agendada';
  const puedeEditarFinalizada = cita.estado === 'finalizada' && puede('agenda.editar_finalizadas');
  const puedeMover = editable && puede('agenda.reprogramar');
  const c = cita.cliente;

  const pie =
    modo === 'ver' ? undefined : (
      <>
        <Button variante="secundario" onClick={() => setModo('ver')}>
          Volver
        </Button>
        <Button
          variante={modo === 'cancelar' ? 'peligro' : 'primario'}
          cargando={accion.isPending}
          onClick={() => accion.mutate()}
          disabled={
            (modo === 'cancelar' && !motivo.trim()) ||
            (modo === 'adelantar' && (!hora || !confirmo))
          }
        >
          {modo === 'adelantar' ? 'Adelantar cita' : 'Cancelar cita'}
        </Button>
      </>
    );

  // El formulario de edición sustituye al panel (anidarlo hace que el panel se cierre al abrirlo).
  if (modo === 'editar') {
    return <NuevaCitaDialog dia={dia} cita={cita} onCerrar={() => setModo('ver')} onCreada={() => onCerrar()} />;
  }

  return (
    <PanelLateral abierto onCambiarAbierto={(v) => !v && onCerrar()} titulo={c.nombre} descripcion={`${nombreServicios(serviciosDe(cita))} · ${formatearMoneda(cobrada ? carrito.data!.total : precioServicios)}`} pie={pie}>
      {modo === 'ver' && (
        <div className="flex flex-col gap-5">
          {puedeEditarFinalizada && (
            <Button variante="secundario" className="self-start" onClick={() => setModo('editar')}>
              <Pencil /> Editar servicio
            </Button>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <EstadoCita estado={cita.estado} />
            {cita.llegoTarde && <span className="text-[12.5px] text-warning">Llegó tarde</span>}
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13.5px]">
            <div className="col-span-2">
              <dt className="text-[12px] text-foreground-muted">Fecha y hora</dt>
              <dd className="cifras first-letter:uppercase">
                {diaLargo(diaNegocio(new Date(cita.inicio)))} · {hora12(new Date(cita.inicio))} – {hora12(new Date(cita.fin))}
              </dd>
            </div>
            <div>
              <dt className="text-[12px] text-foreground-muted">Barbero</dt>
              <dd>{cita.barbero ?? <span className="text-warning">Sin asignar</span>}</dd>
            </div>
            <div>
              <dt className="text-[12px] text-foreground-muted">WhatsApp</dt>
              <dd>
                <a href={enlaceWhatsApp(c.telefono)} target="_blank" rel="noopener noreferrer" className="cifras inline-flex items-center gap-1 text-accent hover:underline">
                  <MessageCircle className="size-3.5" aria-hidden /> {formatearTelefono(c.telefono)}
                </a>
              </dd>
            </div>
          </dl>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              ['Citas', c.citasTotal],
              ['Cancel.', c.cancelaciones],
              ['Retardos', c.retardos],
              ['No vino', c.noShows],
            ].map(([etiqueta, valor]) => (
              <div key={etiqueta} className="rounded-lg bg-surface-alt px-2 py-2">
                <p className="cifras font-display text-[20px] leading-none">{valor}</p>
                <p className="mt-1 text-[11px] text-foreground-muted">{etiqueta}</p>
              </div>
            ))}
          </div>
          {cita.motivoCancelacion && (
            <p className="rounded-md bg-muted px-3 py-2 text-[13px]">
              <span className="font-medium">Motivo:</span> {cita.motivoCancelacion}
            </p>
          )}
          {cita.notas && <p className="text-[13px] text-foreground-muted">{cita.notas}</p>}

          {/* Solo citas que aún esperan al cliente; las históricas (p. ej. importadas) no tienen carrito enlazado. */}
          {!ventaId && (cita.estado === 'agendada' || cita.estado === 'en_tolerancia') && (
            <p className="rounded-md bg-muted px-3 py-2 text-[13px] text-foreground-muted">Sin cobrar</p>
          )}
          {ventaId &&
            verCarrito &&
            (carrito.data ? (
              <DetalleVenta
                venta={carrito.data}
                accionLinea={(l) =>
                  puedeCambiarServicio && l.tipo === 'servicio' && l.servicioId && idsServicios.includes(l.servicioId)
                    ? botonCambiar({ id: l.servicioId, nombre: l.descripcion })
                    : null
                }
              />
            ) : (
              carrito.isLoading && <Esqueleto className="h-32" />
            ))}
          {/* Sin permiso de ver el carrito (el barbero): sus servicios, cada uno con su botón. */}
          {puedeCambiarServicio && !verCarrito && (
            <ul aria-label="Servicios de la cita" className="flex flex-col divide-y divide-border rounded-md border border-border text-[13px]">
              {serviciosDe(cita).map((s) => (
                <li key={s.id} className="px-3 py-2">
                  <p>{s.nombre}</p>
                  {botonCambiar(s)}
                </li>
              ))}
            </ul>
          )}

          {puede('clientes.ver') && (
            <Link to="/clientes" search={{ id: c.id }} className="text-[13px] text-accent hover:underline">
              Ver ficha completa del cliente →
            </Link>
          )}

          {cita.estado === 'en_tolerancia' && restanteTolerancia !== null && (
            <p role="status" className="flex items-center gap-2 rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
              <Hourglass className="size-4 shrink-0" aria-hidden />
              En tolerancia: {restanteTolerancia > 0 ? `quedan ${restanteTolerancia} min para que llegue` : 'se cancelará en un momento'}.
            </p>
          )}

          {(puedeIniciar || (atiende && cita.estado === 'en_servicio')) && (
            <div className="grid gap-2 border-t border-border pt-4">
              <p className="text-[12px] font-medium uppercase tracking-wide text-foreground-muted">Atención</p>
              {puedeIniciar &&
                (cita.barberoId ? (
                  <Button onClick={() => atencion.mutate('iniciar')} cargando={atencion.isPending && atencion.variables === 'iniciar'} disabled={atencion.isPending}>
                    <Play /> Iniciar servicio
                  </Button>
                ) : (
                  <p className="rounded-md bg-muted px-3 py-2 text-[13px] text-foreground-muted">Asigna un barbero para poder iniciar el servicio.</p>
                ))}
              {atiende && cita.estado === 'agendada' && (
                <Button
                  variante="secundario"
                  onClick={() => atencion.mutate('no-presentado')}
                  cargando={atencion.isPending && atencion.variables === 'no-presentado'}
                  disabled={atencion.isPending}
                >
                  <UserX /> El cliente no se ha presentado
                </Button>
              )}
              {cita.estado === 'en_servicio' && (
                <Button onClick={() => setConfirmarFin(true)} disabled={atencion.isPending}>
                  <CircleCheckBig /> Finalizar servicio
                </Button>
              )}
            </div>
          )}

          {puedeMover && (
            <div className="grid gap-2 border-t border-border pt-4">
              <Button variante="secundario" onClick={() => setModo('editar')}>
                <Pencil /> Editar detalles de la cita
              </Button>
              <Button variante="secundario" onClick={() => setModo('adelantar')}>
                <FastForward /> Adelantar (el cliente confirmó)
              </Button>
              {puede('agenda.cancelar') && (
                <Button variante="fantasma" className="text-destructive" onClick={() => setModo('cancelar')}>
                  <CircleSlash /> Cancelar cita
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {modo === 'adelantar' && (
        <div className="flex flex-col gap-4">
          <SelectorHorario
            barberoId={cita.barberoId}
            horaActual={dia === diaNegocio(new Date(cita.inicio)) ? horaLocal(new Date(cita.inicio)) : undefined}
            dia={dia}
            onDia={(d) => {
              setDia(d);
              setChoque(undefined);
            }}
            hora={hora}
            onHora={(h) => {
              setHora(h);
              setChoque(undefined);
            }}
          />
          {choque && (
            <PanelChoque
              barbero={cita.barbero ?? 'el barbero'}
              choque={choque}
              // Adelantar exige la confirmación del cliente, también desde el panel.
              ocupado={accion.isPending || !confirmo}
              onElegir={(h) => {
                setHora(h);
                accion.mutate(h);
              }}
              onVerAgenda={() => setVerAgenda(true)}
            />
          )}
          {verAgenda && cita.barberoId && (
            <VerAgendaDialog
              dia={dia}
              barberoId={cita.barberoId}
              excluirCitaId={cita.id}
              onHora={(h) => {
                setHora(h);
                setChoque(undefined);
              }}
              onCerrar={() => setVerAgenda(false)}
            />
          )}
          <label className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-[13px]">
            <input type="checkbox" className="mt-0.5 size-4 accent-[var(--bb-accent)]" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} />
            Contacté al cliente y confirmó que puede llegar antes.
          </label>
        </div>
      )}

      {modo === 'cancelar' && (
        <Campo etiqueta="Motivo de la cancelación" requerido ayuda="Queda en el historial de cancelaciones del día.">
          {(p) => <Textarea {...p} value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus maxLength={500} />}
        </Campo>
      )}
      {cambiando && <CambiarServicioDialog citaId={cita.id} anterior={cambiando} serviciosCita={idsServicios} onCerrar={() => setCambiando(null)} />}
      {confirmarFin && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={(v) => !v && !atencion.isPending && setConfirmarFin(false)}
          titulo="¿Finalizar el servicio?"
          descripcion={`${c.nombre} · ${nombreServicios(serviciosDe(cita))}${cita.barbero ? ` con ${cita.barbero}` : ''}`}
          pie={
            <>
              <Button variante="secundario" onClick={() => setConfirmarFin(false)} disabled={atencion.isPending}>
                Volver
              </Button>
              <Button onClick={() => atencion.mutate('finalizar')} cargando={atencion.isPending}>
                Sí, finalizar servicio
              </Button>
            </>
          }
        >
          <p role="alert" className="rounded-md bg-warning-soft px-3 py-2.5 text-[13.5px] text-warning">
            Confirma con el barbero la finalización del servicio. Al finalizar se detiene el cronómetro y la venta pasa a caja para cobrarse.
          </p>
        </Dialogo>
      )}
    </PanelLateral>
  );
}
